const RESERVED_LABELS = new Set(['www', 'api']);
const DEFAULT_ROOT_DOMAINS = ['globalerpca.com', 'erpcapro.tech', 'erpca.ca'];

const normalizeHost = (host: string): string => {
  return host.toLowerCase().replace(/:\d+$/, '').replace(/\.$/, '');
};

const getHostFromUrl = (value?: string): string | null => {
  if (!value) {
    return null;
  }

  try {
    return normalizeHost(new URL(value).hostname);
  } catch {
    return null;
  }
};

const isIpv4 = (hostname: string): boolean => {
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
};

const getRootDomains = (apiBaseUrl?: string): string[] => {
  const envDomains = ((import.meta as any).env?.VITE_ROOT_DOMAINS || '')
    .split(',')
    .map((domain: string) => normalizeHost(domain.trim()))
    .filter(Boolean);

  const apiHost = getHostFromUrl(apiBaseUrl || (import.meta as any).env?.VITE_API_BASE_URL);

  return Array.from(new Set([...DEFAULT_ROOT_DOMAINS, ...envDomains, ...(apiHost ? [apiHost] : [])]));
};

const isReservedLabel = (label: string, allowPlatform: boolean): boolean => {
  if (RESERVED_LABELS.has(label)) {
    return true;
  }

  if (!allowPlatform && label === 'platform') {
    return true;
  }

  return false;
};

interface ExtractSubdomainOptions {
  allowPlatform?: boolean;
  apiBaseUrl?: string;
}

export const extractTenantSubdomain = (
  hostname: string,
  options: ExtractSubdomainOptions = {}
): string | null => {
  const normalizedHost = normalizeHost(hostname);
  const allowPlatform = options.allowPlatform ?? false;

  if (!normalizedHost || normalizedHost === 'localhost' || normalizedHost === '127.0.0.1' || isIpv4(normalizedHost)) {
    return null;
  }

  if (normalizedHost.endsWith('.localhost')) {
    const localParts = normalizedHost.split('.');
    if (localParts.length === 2 && !isReservedLabel(localParts[0], allowPlatform)) {
      return localParts[0];
    }

    return null;
  }

  const rootDomains = getRootDomains(options.apiBaseUrl);

  for (const rootDomain of rootDomains) {
    if (normalizedHost === rootDomain || normalizedHost === `www.${rootDomain}`) {
      return null;
    }

    const suffix = `.${rootDomain}`;
    if (normalizedHost.endsWith(suffix)) {
      const prefix = normalizedHost.slice(0, -suffix.length);
      if (!prefix || prefix.includes('.') || isReservedLabel(prefix, allowPlatform)) {
        return null;
      }

      return prefix;
    }
  }

  const parts = normalizedHost.split('.');
  if (parts.length >= 3) {
    const candidate = parts[0];
    return isReservedLabel(candidate, allowPlatform) ? null : candidate;
  }

  return null;
};
